/**
 * Hosted Auth Helpers
 *
 * Utilities for detecting hosted auth environment and building legacy auth URLs
 * for cross-domain authentication flows.
 */

/**
 * Get CSRF token from cookie
 *
 * Reads the insforge_csrf_token cookie set by the backend after authentication.
 * This is needed for OAuth flows where the csrfToken is stored in cookies
 * rather than returned in the API response.
 *
 * @returns The CSRF token value or null if not found
 */
export function getCsrfTokenFromCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(/(?:^|;\s*)insforge_csrf_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Check if current environment is a hosted auth environment
 *
 * Returns true for:
 * - localhost with port 7130 (hosted auth app dev)
 * - https://*.insforge.app (hosted auth app production)
 *
 * @returns true if running in hosted auth environment
 */
export function isHostedAuthEnvironment(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const { hostname, port, protocol } = window.location;

  // Local development
  if (hostname === 'localhost' && port === '7130') {
    return true;
  }

  // Production hosted auth
  if (protocol === 'https:' && hostname.endsWith('.insforge.app')) {
    return true;
  }

  return false;
}

/**
 * Session data for building legacy auth URL
 */
export interface LegacyAuthSession {
  accessToken: string;
  userId: string;
  email: string;
  name?: string;
  csrfToken?: string;
}

/**
 * Build a legacy flow redirect URL with auth params in query string
 *
 * This is used to pass authentication credentials back to the user's app
 * after OAuth completes in the hosted auth environment.
 *
 * @param redirectUrl - The URL to redirect to (user's app)
 * @param session - The session data to include in the URL
 * @returns The complete URL with auth params
 */
export function buildLegacyAuthUrl(redirectUrl: string, session: LegacyAuthSession): string {
  const url = new URL(redirectUrl);
  url.searchParams.set('access_token', session.accessToken);
  url.searchParams.set('user_id', session.userId);
  url.searchParams.set('email', session.email);

  if (session.name) {
    url.searchParams.set('name', session.name);
  }

  if (session.csrfToken) {
    url.searchParams.set('csrf_token', session.csrfToken);
  }

  return url.toString();
}
